// ============================================
// Path: /frontend/src/pages/UpgradePlanPage.jsx
// Purpose: Display available upgrade plans (only higher tiers)
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import api from '../api/client';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle, TrendingUp, ArrowRight, ChevronDown } from 'lucide-react';

export default function UpgradePlanPage() {
  console.log('[UpgradePlanPage] Component mounted');

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      console.log('[UpgradePlanPage] Fetching plans and current subscription');

      // Fetch available plans
      const plansResponse = await api.get('/subscription/plans');
      const allPlans = plansResponse.data.plans;

      // Fetch current subscription
      const subResponse = await api.get('/subscription/current');
      const currentSub = subResponse.data.subscription;

      console.log('[UpgradePlanPage] Current tier:', currentSub.tier);
      setCurrentSubscription(currentSub);

      // Filter to show only higher tiers
      const tierOrder = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM'];
      const currentIndex = tierOrder.indexOf(currentSub.tier);
      
      const availableUpgrades = allPlans.filter((plan) => {
        const planIndex = tierOrder.indexOf(plan.tier);
        return planIndex > currentIndex;
      });

      console.log('[UpgradePlanPage] Available upgrades:', availableUpgrades.length);
      setPlans(availableUpgrades);

    } catch (error) {
      console.error('[UpgradePlanPage] Fetch error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load subscription plans.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = (plan) => {
    console.log('[UpgradePlanPage] Upgrade clicked:', plan.tier);

    // Navigate to subscription checkout with plan details
    navigate('/subscription/checkout', {
      state: { plan }
    });
  };

  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-left mb-4 sm:mb-6">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">Upgrade Your Plan</h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground mb-3">
              Choose a higher tier to unlock more features
            </p>

            {/* Current Plan Badge */}
            {currentSubscription && (
              <div className="inline-flex items-center gap-2 bg-muted/40 px-3 py-1.5 rounded-lg">
                <span className="text-xs sm:text-sm font-medium">Current Plan:</span>
                <Badge variant="secondary" className="text-xs sm:text-sm">
                  {currentSubscription.tier}
                </Badge>
                {currentSubscription.endDate && (
                  <span className="text-[10px] sm:text-xs text-muted-foreground">
                    (Valid until {new Date(currentSubscription.endDate).toLocaleDateString()})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* No Upgrades Available */}
          {plans.length === 0 && (
            <Card className="max-w-2xl mx-auto">
              <CardHeader className="text-center p-3 sm:p-4 md:p-6">
                <CheckCircle className="h-12 w-12 sm:h-14 sm:w-14 text-green-600 mx-auto mb-3" />
                <CardTitle className="text-base sm:text-lg md:text-xl font-bold">You're on the Highest Plan!</CardTitle>
                <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                  You're already enjoying all the premium features. Thank you for your support!
                </CardDescription>
              </CardHeader>
              <CardFooter className="justify-center p-3 sm:p-4 md:p-6">
                <Button onClick={() => navigate('/dashboard')} className="text-xs sm:text-sm h-9 sm:h-10">
                  Go to Dashboard
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* Available Upgrade Plans */}
          {plans.length > 0 && (
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col gap-2 sm:gap-3">
                {plans.map((plan) => (
                  <div
                    key={plan.tier}
                    className={`bg-white border-2 rounded-lg transition-all ${
                      plan.popular ? 'border-primary shadow-md' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Most Popular Badge */}
                    {plan.popular && (
                      <div className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-t-lg text-center">
                        Most Popular
                      </div>
                    )}

                    {/* Clickable Header - Always Visible */}
                    <button
                      onClick={() => setExpandedPlan(expandedPlan === plan.tier ? null : plan.tier)}
                      className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          {/* Icon */}
                          <div className="flex-shrink-0 text-xl sm:text-2xl">
                            {plan.icon}
                          </div>
                          
                          {/* Tier Name */}
                          <div className="flex-shrink-0">
                            <span className="text-xs sm:text-sm md:text-base font-bold text-gray-900">
                              {plan.name}
                            </span>
                          </div>
                          
                          {/* Price */}
                          <div className="flex-shrink-0">
                            <span className="text-xs sm:text-sm md:text-base font-bold text-primary">
                              KES {plan.price.toLocaleString()}
                            </span>
                          </div>
                          
                          {/* Duration */}
                          <div className="hidden sm:block flex-shrink-0">
                            <span className="text-xs sm:text-sm text-muted-foreground">
                              /month
                            </span>
                          </div>
                          
                          {/* Listings */}
                          <div className="flex-shrink-0">
                            <span className="text-xs sm:text-sm text-muted-foreground">
                              {plan.adsLimit || 'Unlimited'} listings
                            </span>
                          </div>
                        </div>
                        
                        {/* Expand Indicator */}
                        <div className="flex-shrink-0">
                          <ChevronDown
                            className={`w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground transition-transform ${
                              expandedPlan === plan.tier ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </div>
                    </button>

                    {/* Collapsible Content */}
                    {expandedPlan === plan.tier && (
                      <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
                        <div className="flex flex-col gap-3 sm:gap-4">
                          {/* Features List */}
                          <ul className="flex flex-col gap-2">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>

                          {/* Upgrade Button */}
                          <Button
                            className="w-full"
                            onClick={() => handleUpgradeClick(plan)}
                            variant={plan.popular ? 'default' : 'outline'}
                          >
                            <TrendingUp className="mr-2 h-4 w-4" />
                            Upgrade to {plan.name}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Help Section - Expandable */}
          <div className="mt-4 max-w-3xl mx-auto">
            <Card className="bg-muted/40">
              {/* Clickable Header */}
              <CardHeader 
                className="p-2 sm:p-3 cursor-pointer hover:bg-accent/50 transition-all duration-200 relative"
                onClick={() => setShowHelp(!showHelp)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs sm:text-sm font-semibold">How Upgrades Work</CardTitle>
                  <ChevronDown
                    className={`w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground transition-transform ${
                      showHelp ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </CardHeader>

              {/* Collapsible Content */}
              {showHelp && (
                <CardContent className="p-2 sm:p-3 pt-0">
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <div className="bg-primary/10 rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary text-[10px] sm:text-xs">1</span>
                      </div>
                      <div>
                        <p className="font-medium mb-0.5 text-[10px] sm:text-xs">Select a Higher Plan</p>
                        <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">Choose any plan above your current tier</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="bg-primary/10 rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary text-[10px] sm:text-xs">2</span>
                      </div>
                      <div>
                        <p className="font-medium mb-0.5 text-[10px] sm:text-xs">Complete Payment</p>
                        <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">Pay via M-Pesa on the checkout page</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="bg-primary/10 rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary text-[10px] sm:text-xs">3</span>
                      </div>
                      <div>
                        <p className="font-medium mb-0.5 text-[10px] sm:text-xs">Instant Activation</p>
                        <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">Your new plan activates immediately after payment</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="bg-primary/10 rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary text-[10px] sm:text-xs">4</span>
                      </div>
                      <div>
                        <p className="font-medium mb-0.5 text-[10px] sm:text-xs">Enjoy New Limits</p>
                        <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">Access increased limits for 30 days</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}